import { useCallback, useContext, useEffect, useState } from 'react';
import { CopyIcon, SuccessIcon, LocationIcon, InfoIcon, ArrowUpIcon } from 'app/agriculture/icons';
import { ServicesContext } from 'app/items/shared/context_interface';
import { MousePosition } from './ContextMenu';
import { ContextMenuItem } from './ContextMenuItem';
import { AgricultureToastType } from 'app/agriculture/AgricultureToast/agriculture_toast_service';

interface ContextMenuStats {
    latitude: number;
    longitude: number;
    height?: number | null;
}

interface ContextMenuStatsProps {
    mousePosition: MousePosition;
}

export const ContextMenuStats = ({ mousePosition: { x, y } }: ContextMenuStatsProps) => {
    const { mapService, agricultureToastService } = useContext(ServicesContext);
    const [stats, setStats] = useState<ContextMenuStats | null>(null);
    const [coordinatesCopied, setCoordinatesCopied] = useState(false);

    useEffect(() => {
        async function getStatsFromPosition() {
            const { lat, lng } = mapService.mousePositionToLocation(x, y);

            if (lat == null || lng == null) {
                return null;
            }

            const height = await mapService.getHeight(lng, lat);

            return {
                latitude: lat,
                longitude: lng,
                height,
            };
        }

        let shouldUpdate = true;
        getStatsFromPosition()
            .then((stats) => {
                if (shouldUpdate) {
                    setStats(stats);
                }
            })
            .catch((e: unknown) => {
                console.error(e);
            });

        return () => {
            shouldUpdate = false;
        };
    }, [x, y, mapService]);

    const handleCoordinatesClick = useCallback(async () => {
        if (!stats) return;
        const { latitude, longitude } = stats;
        const coordinateString = [latitude, longitude].map((e) => e.toFixed(5)).join(', ');
        await navigator.clipboard.writeText(coordinateString);
        setCoordinatesCopied(true);
        await agricultureToastService.showToast({
            title: 'Location copied:',
            subtext: coordinateString,
            type: AgricultureToastType.SUCCESS,
        });
    }, [agricultureToastService, stats]);

    if (!stats) return null;
    const { latitude, longitude, height } = stats;
    const coordinateString = [latitude, longitude].map((e) => e.toFixed(5)).join(', ');

    return (
        <>
            <ContextMenuItem
                Icon={LocationIcon}
                SecondIcon={coordinatesCopied ? SuccessIcon : CopyIcon}
                title={coordinateString}
                onClick={() => {
                    void handleCoordinatesClick();
                }}
                id="ContextMenuStats-location"
            />
            {height == null ? null : (
                <ContextMenuItem
                    Icon={ArrowUpIcon}
                    SecondIcon={InfoIcon}
                    title={`${height.toFixed(1)}m elevation`}
                    tooltip={'above WGS84 ellipsoid'}
                />
            )}
        </>
    );
};
